import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { format } from "date-fns";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Cslayout";

// Task Form Component
const TaskForm = ({
  onSubmitTask,
  clients,
  selectedClientId,
  onClientChange,
  isSubmitting,
}) => {
  const [formState, setFormState] = useState({
    title: "",
    description: "",
    dueDate: "",
    priority: "medium",
    attachments: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handlePriorityChange = (e) => {
    setFormState((prev) => ({ ...prev, priority: e.target.value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        attachments: Array.from(e.target.files || []),
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmitTask(formState);

    setFormState({
      title: "",
      description: "",
      dueDate: "",
      priority: "medium",
      attachments: [],
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      <FormControl fullWidth required>
        <InputLabel>Client</InputLabel>
        <Select
          value={selectedClientId}
          label="Client"
          onChange={onClientChange}
        >
          {clients.map((client) => (
            <MenuItem key={client._id} value={client._id}>
              {client.username} ({client.email})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Task Title"
        name="title"
        value={formState.title}
        onChange={handleInputChange}
        required
        fullWidth
      />

      <TextField
        label="Description"
        name="description"
        value={formState.description}
        onChange={handleInputChange}
        required
        fullWidth
        multiline
        rows={4}
      />

      <TextField
        label="Due Date"
        type="date"
        name="dueDate"
        value={formState.dueDate}
        onChange={handleInputChange}
        required
        fullWidth
        InputLabelProps={{ shrink: true }}
        inputProps={{
          min: new Date().toISOString().split("T")[0],
        }}
      />

      <FormControl fullWidth>
        <InputLabel>Priority</InputLabel>
        <Select
          value={formState.priority}
          label="Priority"
          onChange={handlePriorityChange}
        >
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="urgent">Urgent</MenuItem>
        </Select>
      </FormControl>

      <Button variant="contained" component="label" startIcon={<AddIcon />}>
        Attachments ({formState.attachments.length})
        <input type="file" hidden multiple onChange={handleFileChange} />
      </Button>

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
      >
        {isSubmitting ? "Assigning..." : "Assign Task"}
      </Button>
    </Box>
  );
};

// Task List Component
const TaskList = React.memo(
  ({
    tasks,
    loading,
    viewMode,
    onViewModeChange,
    selectedClient,
    statusConfig,
    priorityConfig,
    getAssigneeName,
    handleStatusUpdate,
    confirmDeleteTask,
  }) => {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              pb: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6">Tasks</Typography>
            <Box>
              <Button
                variant={viewMode === "all" ? "contained" : "outlined"}
                color="primary"
                sx={{ mr: 1 }}
                onClick={() => onViewModeChange("all")}
              >
                All Tasks
              </Button>
              <Button
                variant={viewMode === "client" ? "contained" : "outlined"}
                color="primary"
                onClick={() => onViewModeChange("client")}
                disabled={!selectedClient}
              >
                Client Tasks
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <Typography variant="h6" color="textSecondary">
                No tasks found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Assign a new task to get started
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {tasks.map((task) => (
                <Card key={task._id} variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">{task.title}</Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip
                          label={
                            priorityConfig[task.priority]?.label ||
                            task.priority
                          }
                          color={
                            priorityConfig[task.priority]?.color || "default"
                          }
                          size="small"
                        />
                        <Chip
                          label={
                            statusConfig[task.status]?.label || task.status
                          }
                          color={statusConfig[task.status]?.color || "default"}
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {task.description}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Due:</strong>{" "}
                          <Box
                            component="span"
                            sx={{
                              color:
                                new Date(task.dueDate) < new Date() &&
                                task.status !== "completed"
                                  ? "error.main"
                                  : "inherit",
                            }}
                          >
                            {format(new Date(task.dueDate), "MMM dd, yyyy")}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Assigned to:</strong> {getAssigneeName(task)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={task.status}
                          label="Status"
                          onChange={(e) =>
                            handleStatusUpdate(task._id, e.target.value)
                          }
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="in-progress">In Progress</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="overdue">Overdue</MenuItem>
                        </Select>
                      </FormControl>

                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton color="primary">
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => confirmDeleteTask(task._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }
);

// Main Component
const ClientTasks = () => {
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [clientMap, setClientMap] = useState({});
  const isFetchingRef = useRef(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const statusConfig = useMemo(
    () => ({
      pending: { color: "warning", label: "Pending" },
      "in-progress": { color: "info", label: "In Progress" },
      completed: { color: "success", label: "Completed" },
      overdue: { color: "error", label: "Overdue" },
    }),
    []
  );

  const priorityConfig = useMemo(
    () => ({
      low: { color: "default", label: "Low" },
      medium: { color: "primary", label: "Medium" },
      high: { color: "warning", label: "High" },
      urgent: { color: "error", label: "Urgent" },
    }),
    []
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const processTasksWithClientData = useCallback((tasksList, clientIdMap) => {
    return tasksList.map((task) => {
      if (typeof task.assignedTo === "string" && clientIdMap[task.assignedTo]) {
        return {
          ...task,
          clientData: clientIdMap[task.assignedTo],
        };
      }
      return task;
    });
  }, []);

  const fetchTasks = useCallback(
    async (mode = "all", clientId = null) => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;

      try {
        setLoading(true);

        const url =
          mode === "all"
            ? "/api/v1/tasks/assigned"
            : `/api/v1/tasks/client/${clientId}`;

        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const tasksList = Array.isArray(data) ? data : [];

        const processedTasks = processTasksWithClientData(
          tasksList,
          clientMap
        );
        setTasks(processedTasks);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        showSnackbar("Failed to fetch tasks", "error");
        setTasks([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [clientMap, processTasksWithClientData]
  );

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;

      try {
        setLoading(true);

        // Fetch clients and tasks
        const [clientsResponse, tasksResponse] = await Promise.all([
          fetch("/api/v1/users/demo/clients"),
          fetch("/api/v1/tasks/assigned"),
        ]);

        if (!clientsResponse.ok) {
          throw new Error(`Failed to fetch clients: ${clientsResponse.status}`);
        }
        
        if (!tasksResponse.ok) {
          throw new Error(`Failed to fetch tasks: ${tasksResponse.status}`);
        }

        const clientsData = await clientsResponse.json();
        const tasksData = await tasksResponse.json();

        // Handle clients
        const clientsList = clientsData.data?.clients || [];
        setClients(clientsList);

        const clientIdMap = {};
        clientsList.forEach((client) => {
          clientIdMap[client._id] = client;
        });
        setClientMap(clientIdMap);

        // Handle tasks
        const tasksList = Array.isArray(tasksData) ? tasksData : [];

        const processedTasks = tasksList.map((task) => {
          if (
            typeof task.assignedTo === "string" &&
            clientIdMap[task.assignedTo]
          ) {
            return {
              ...task,
              clientData: clientIdMap[task.assignedTo],
            };
          }
          return task;
        });

        setTasks(processedTasks);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        showSnackbar("Failed to load data", "error");
        setClients([]);
        setTasks([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchInitialData();
  }, []);

  const handleClientChange = useCallback(
    async (e) => {
      const clientId = e.target.value;
      setSelectedClient(clientId);

      if (clientId && viewMode === "client") {
        await fetchTasks("client", clientId);
      }
    },
    [viewMode, fetchTasks]
  );

  const handleViewModeChange = useCallback(
    async (mode) => {
      setViewMode(mode);
      if (mode === "all") {
        await fetchTasks("all");
      } else if (mode === "client" && selectedClient) {
        await fetchTasks("client", selectedClient);
      }
    },
    [fetchTasks, selectedClient]
  );

  const handleTaskSubmit = async (formData) => {
    if (!selectedClient) {
      showSnackbar("Please select a client", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("assignedTo", selectedClient);
      formDataToSend.append("dueDate", formData.dueDate);
      formDataToSend.append("priority", formData.priority);

      formData.attachments.forEach((file) => {
        formDataToSend.append("attachments", file);
      });

      const response = await fetch("/api/v1/tasks", {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newTask = await response.json();

      // Add client data to the new task
      const taskWithClientData = {
        ...newTask,
        clientData: clients.find((client) => client._id === selectedClient),
      };

      setTasks((prev) => [taskWithClientData, ...prev]);
      showSnackbar(`Task "${formData.title}" created successfully!`);
    } catch (error) {
      console.error("Error creating task:", error);
      showSnackbar("Failed to create task", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      // Optimistic update
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId ? { ...task, status: newStatus } : task
        )
      );

      const response = await fetch(`/api/v1/tasks/${taskId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      showSnackbar("Task status updated successfully!");
    } catch (error) {
      console.error("Error updating task status:", error);
      showSnackbar("Failed to update task status", "error");
      // Revert optimistic update
      await fetchTasks(viewMode, viewMode === "client" ? selectedClient : null);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      const response = await fetch(`/api/v1/tasks/${taskToDelete}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove task from state
      setTasks((prev) => prev.filter((task) => task._id !== taskToDelete));

      setOpenDeleteDialog(false);
      setTaskToDelete(null);
      showSnackbar("Task deleted successfully!");
    } catch (error) {
      console.error("Error deleting task:", error);
      showSnackbar("Failed to delete task", "error");
    }
  };

  const confirmDeleteTask = useCallback((taskId) => {
    setTaskToDelete(taskId);
    setOpenDeleteDialog(true);
  }, []);

  const getAssigneeName = useCallback(
    (task) => {
      if (task.clientData && task.clientData.username) {
        return task.clientData.username;
      }

      if (typeof task.assignedTo === "object" && task.assignedTo !== null) {
        return (
          task.assignedTo.username ||
          task.assignedTo.name ||
          task.assignedTo.email ||
          "Unknown"
        );
      }

      if (typeof task.assignedTo === "string" && clientMap[task.assignedTo]) {
        return clientMap[task.assignedTo].username;
      }

      if (task.assignedTo) {
        return `Client (ID: ${task.assignedTo.toString().substring(0, 8)}...)`;
      }

      return "Unknown";
    },
    [clientMap]
  );

  return (
    <Layout>
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: 3, py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: "bold", mb: 1 }}
          >
            Task Management
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={4}>
            <Card variant="outlined" sx={{ height: "fit-content" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  Assign New Task
                </Typography>

                <TaskForm
                  onSubmitTask={handleTaskSubmit}
                  clients={clients}
                  selectedClientId={selectedClient}
                  onClientChange={handleClientChange}
                  isSubmitting={isSubmitting}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <TaskList
              tasks={tasks}
              loading={loading}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              selectedClient={selectedClient}
              statusConfig={statusConfig}
              priorityConfig={priorityConfig}
              getAssigneeName={getAssigneeName}
              handleStatusUpdate={handleStatusUpdate}
              confirmDeleteTask={confirmDeleteTask}
            />
          </Grid>
        </Grid>

        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Delete Task</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this task? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteTask}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ClientTasks;
